import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { useContext, useEffect } from 'react';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
import { useMQTTChannelMessagesV2 } from '../../hooks/useMQTTChannelMessagesV2';
import { ProcessReceptionAreaRequestStatus } from './CloudSideTypes';
import { CoverageReportProvider, summarizeContext } from './Contexts';
import { NetworkCoverageContext } from './NetworkCoverageContext';
import { ReportViewer } from './Display/ReportView';
import { NetworkCoverageMap } from './Map/NetworkCoverageMapV3';
import { useCoverage } from './ResultRetrieval/hooks/useCoverage';
import { PropertySelector } from './Selection/CoverageObjects/Variants/PropertySelector';
import { CoverageReportSelector } from './Selection/CoverageReports/CoverageReportSelector';
import { ParameterSelectorPopup } from './Selection/Parameters/ParameterSelectorPopup';
import { CoverageStepper } from './subComponents/CoverageStepper';
import { RequestStatusPopup } from './ResultRetrieval/RequestStatusPopup';
const debug = process.env.REACT_APP_DEBUG && false;
const { REACT_APP_PROJECT_CODE: projectCode, REACT_APP_DEPLOYMENT_STAGE: stage } = process.env;
export const NetworkCoverageWizard = ({}) => {
    // main app state
    const [{ contextCustomer, contextUser, fullscreen, isSmallView }, appMainViewDispatch] = useContext(AppMainViewContext);
    // coverage reports state
    const { state, dispatch } = useContext(NetworkCoverageContext);
    const { Areas, Places, Properties, requestStatusMap, activeStep } = state;
    /* I decided to keep coverage reports as their own context because they're not actually a reducer. I could have effects in this file that capture the output of the hook and call the relevant dispatches to NetworkCoverageContext but it doesn't really seem worth it. */
    const { retrievingReports, coverageReportMap, selectedReports, selectedReportsHash, setSelectedReports } = useCoverage({
        coverageGeoObjects: 0 < activeStep ? [...Areas, ...Places] : [],
        requestStatusMap,
    });
    //debug && console.log('NetworkCoverageWizard coverageReportMap', coverageReportMap)
    const updateRequestStatus = (resultNotification) => {
        const { requestId, GeoObject, status } = resultNotification;
        debug &&
            console.log(`NetworkCoverageWizard: updateRequestStatus adding "${GeoObject.name}" result: ${ProcessReceptionAreaRequestStatus[status]}.`);
        dispatch({ type: 'addCoverageResult', result: resultNotification });
    };
    useMQTTChannelMessagesV2({
        user: contextUser,
        topicGetter: (users) => users.map((user) => `${projectCode}/${stage}/user/${user.id}`),
        addToParentItems: updateRequestStatus,
        topicGenerationObjects: contextUser ? [contextUser] : [],
    });
    /** Network Coverage wizard steps */
    const steps = [
        { label: isSmallView ? 'Select' : 'Select Property', desc: 'Choose a property from the dropdown.' },
        { label: isSmallView ? 'Review' : 'Review Coverage', desc: 'Review coverage report results on the map.' },
        { label: isSmallView ? 'Table' : 'Table & Export', desc: 'View and export coverage reports from a table.' },
    ];
    const propertySelection = (_jsxs(Stack, { gap: 1, children: [_jsx(PropertySelector, { contextCustomer: contextCustomer }), _jsx(NetworkCoverageMap, {}, "coverageMap")] }));
    const coverageMap = (_jsxs(Stack, { gap: 1, children: [_jsx(ParameterSelectorPopup, {}), _jsx(RequestStatusPopup, {}), _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', gap: 1, children: [_jsx(CoverageReportSelector, {}), _jsx(NetworkCoverageMap, {}, "coverageMap")] })] }));
    const reportViewing = (_jsx(_Fragment, { children: _jsx(ReportViewer, {}) }));
    const stepViews = [propertySelection, coverageMap, reportViewing];
    // hide app drawer on this page
    useEffect(() => {
        const wasFullscreen = structuredClone(fullscreen);
        const newPartialState = { fullscreen: true, navDrawerOpen: false };
        appMainViewDispatch(newPartialState);
        return () => {
            // don't un-fullscreen if we were already in fullscreen mode
            if (wasFullscreen)
                return;
            const newPartialState = { fullscreen: false, navDrawerOpen: false };
            appMainViewDispatch(newPartialState);
        };
    }, []);
    return (_jsx(CoverageReportProvider, { retrievingReports, coverageReportMap, selectedReports, selectedReportsHash, setSelectedReports, children: _jsx(Box, { maxWidth: "100cqw", children: _jsx(Card, { sx: { boxShadow: isSmallView ? 'none' : 'default' }, children: _jsx(CardContent, { sx: isSmallView ? { margin: 0, padding: 0 } : {}, children: _jsxs(Stack, { gap: 1, children: [_jsx(Typography, { variant: "h5", children: "Coverage" }), _jsxs(CoverageStepper, { steps: steps, children: [stepViews[activeStep], debug && _jsx(Typography, { sx: { marginBlockStart: 1 }, children: summarizeContext(state) })] })] }) }) }) }) }));
};
